import React, {useEffect, useState, useRef} from "react";
import "./TopicDescriptionTest.css";
import {FormattedMessage, useIntl} from "react-intl";
import API from "../logic/API";
import successIcon from "../images/success.svg";
import errorIcon from "../images/error.svg";
import {Button, Icon} from "@spg/component_library";

type TopicDescriptionTestProps = {
  /** An index to avoid HTML-id collisions in lists */
  index: number;
  /** The id of the topic description */
  id: string;
};

/**
 * An externally labelled button to test the transfer configuration of a topic description
 */
function TopicDescriptionTest(props: TopicDescriptionTestProps) {
  const intl = useIntl();
  const [testStatus, setTestStatus] = useState<
    "SUCCESS" | "PENDING" | "ERROR" | "CANCEL" | "IDLE"
  >("IDLE");
  const [timestamp, setTimestamp] = useState<number>();
  const abortController = useRef<AbortController>(new AbortController());
  const testConnection = (id: string) => {
    const currentAbortController = abortController.current;
    API.testTopicConnection(id, currentAbortController).then((resolution) => {
      if (resolution.success) {
        setTestStatus((currentState) =>
          currentState === "PENDING" ? "SUCCESS" : currentState,
        );
      } else {
        setTestStatus((currentState) =>
          currentState === "PENDING" ? "ERROR" : currentState,
        );
      }
    });
  };

  useEffect(() => setTimestamp(Date.now()), [testStatus]);

  return (
    <div className="topicDescriptionTestInteractionElementContainer">
      <div className="topicDescriptionTestHeader">
        <FormattedMessage id="topicDescriptionTest.header" />
      </div>
      <div className="topicDescriptionTestInteractionTestContainer">
        <Button
          label={intl.formatMessage({
            id:
              testStatus === "PENDING"
                ? "topicDescriptionTest.cancel"
                : "topicDescriptionTest.start",
          })}
          idBase={`testConnection_${props.id}_`}
          onClick={() => {
            if (testStatus === "PENDING") {
              setTestStatus("CANCEL");
              abortController.current.abort();
              abortController.current = new AbortController();
            } else {
              setTestStatus("PENDING");
              setTimestamp(Date.now());
              testConnection(props.id);
            }
          }}
          configuration={"secondary"}
        />
        <div
          className="topicDescriptionTestStatusContainer"
          id={`statusContainer_${props.id}_${testStatus}`}
        >
          {testStatus === "PENDING" && (
            <div className="topicDescriptionTestLoadingSpinner"></div>
          )}
          {testStatus === "SUCCESS" && (
            <Icon
              href={successIcon + "#globalG"}
              iconSize={{width: 40, height: 40}}
              fillColor="#00AF89"
              title=""
            />
          )}
          {(testStatus === "ERROR" || testStatus === "CANCEL") && (
            <Icon
              href={errorIcon + "#globalG"}
              iconSize={{width: 40, height: 40}}
              fillColor="#B32424"
              title=""
            />
          )}
          {testStatus !== "IDLE" && (
            <div className="topicDescriptionTestStatusInfo">
              <FormattedMessage id={`topicDescriptionTest.${testStatus}`} />
              <div>
                {Intl.DateTimeFormat(intl.locale, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                }).format(timestamp)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export {TopicDescriptionTest};
